import { useCallback, useReducer, useEffect, useRef } from 'react';
import { notification } from 'antd';
import { cond, equals, T, toString } from 'ramda';
import { CancelToken } from 'axios';
import ajax from '../../utils/network';
import useDidMount from './useDidMount';

const actions = {
  FETCH_START: 'FETCH_START',
  FETCH_END: 'FETCH_END',
  FETCH_UPDATE: 'FETCH_UPDATE',
};

const initialState = (auto) => ({
  loading: auto,
  error: undefined,
  data: undefined,
  canceled: false,
  abort: () => {},
});

const reducer = (state, action) =>
  cond([
    [equals(actions.FETCH_START), () => ({ ...state, loading: true })],
    [equals(actions.FETCH_END), () => ({ ...state, loading: false })],
    [equals(actions.FETCH_UPDATE), () => ({ ...state, ...action.payload })],
    [T, () => state],
  ])(action.type);

const useAjax = (config) => {
  const { silence = false, autoFetch = false, autoCancel = true, ...options } = config;
  const optionsString = toString(options);
  const [state, dispatch] = useReducer(reducer, initialState(autoFetch));

  // level-component. cancel the request when componentWillUnmount.
  const abortRef = useRef(() => {});
  useEffect(() => {
    // !focus: 'Request aborted' below must not modified. its under the rules of axios cancel message.
    return async () => {
      abortRef.current('Request aborted automatically while componentWillUnmount.');
    };
  }, []);

  const memorizedAjaxRequest = useCallback(
    () =>
      ((optionsString, autoCancel) => {
        const optionsParsed = JSON.parse(optionsString);
        const targetOptions = {
          ...optionsParsed,
          cancelToken: new CancelToken(function executor(c) {
            autoCancel && (abortRef.current = c);
            // An executor function receives a cancel function as a parameter
            dispatch({ type: actions.FETCH_UPDATE, payload: { abort: c } });
          }),
        };
        return ajax(targetOptions);
      })(optionsString, autoCancel),
    [optionsString, autoCancel],
  );

  const fetch = async () => {
    try {
      dispatch({ type: actions.FETCH_START });
      const { success, message, data, status, canceled } = await memorizedAjaxRequest();
      if (status === 401) {
        // todo: special execute ???
      }
      // notify
      if (!success && !status && !canceled) {
        // network error | timeout ...others
        !silence &&
          notification.error({
            message: '发生异常',
            description: message,
          });
      } else if (!success && status && status >= 300 && !canceled) {
        // 401 | 404 | 500 | other logical error
        !silence &&
          notification.error({
            message: '操作失败',
            description: message,
          });
      }
      // if success, resetting error.
      dispatch({ type: actions.FETCH_UPDATE, payload: { error: undefined, data, canceled } });
    } catch (error) {
      // if success, resetting data.
      dispatch({
        type: actions.FETCH_UPDATE,
        payload: { error, data: undefined, canceled: false },
      });
    } finally {
      dispatch({ type: actions.FETCH_END });
    }
  };

  useDidMount(() => {
    autoFetch && fetch();
  });

  return [state, fetch];
};

export default useAjax;
